import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { versions } from 'virtual:release-notes';
import { ChangelogContent } from '../components/ChangelogContent';
import { AuthoredHtml } from '../components/AuthoredHtml';

type Tab = 'release-notes' | 'benchmarks';

const TABS: { id: Tab; label: string }[] = [
  { id: 'release-notes', label: 'Release Notes' },
  { id: 'benchmarks', label: 'Benchmark Results' },
];

/**
 * Renders the release note for a single version. The `:version` route param
 * is matched against the changelog entries baked in at build time. Content is
 * split across two tabs: "Release Notes" (changelog bullets + authored notes)
 * and "Benchmark Results" (authored benchmark HTML).
 */
export function ReleaseNoteVersionPage() {
  const { version } = useParams<{ version: string }>();
  const entry = versions.find((v) => v.version === version);
  const [tab, setTab] = useState<Tab>('release-notes');

  // Reset to the first tab whenever the selected version changes so switching
  // versions doesn't strand the user on a tab that may be empty.
  useEffect(() => {
    setTab('release-notes');
  }, [version]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        {entry ? (
          <>
            <div className="mb-4 flex items-center gap-3">
              <h2 className="text-lg font-semibold text-slate-900">
                Version {entry.version}
              </h2>
              <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 font-mono text-xs text-indigo-600">
                v{entry.version}
              </span>
            </div>

            <div className="mb-5 flex gap-1 border-b border-slate-200">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                    tab === t.id
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'release-notes' ? (
              <>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Changelog
                </h3>
                <ChangelogContent body={entry.body} />
                {entry.html ? (
                  <div className="mt-6 border-t border-slate-100 pt-5">
                    <AuthoredHtml html={entry.html} />
                  </div>
                ) : null}
              </>
            ) : entry.benchmarksHtml ? (
              <AuthoredHtml html={entry.benchmarksHtml} />
            ) : (
              <p className="text-sm text-slate-500">
                No benchmark results for version {entry.version} yet.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-slate-900">
              Version not found
            </h2>
            <p className="text-sm text-slate-500">
              {version
                ? `No release notes found for version "${version}".`
                : 'No version specified.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
